import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { X, Search, Hash, FileText, Send, Check } from 'lucide-react'
import { chatService } from '../../services/chatService'
import { toast } from '../../stores/uiStore'
import { avatarGradient, initials } from '../crm/pipeline/tokens'

// Mesajı başka kanal/kişilere ilet (ClickUp "Forward a message")
export default function ForwardModal({ company, message, onClose, onSent }) {
  const [q, setQ] = useState('')
  const [note, setNote] = useState('')
  const [pickedCh, setPickedCh] = useState([])
  const [pickedU, setPickedU] = useState([])
  const [busy, setBusy] = useState(false)

  const { data: convData } = useQuery({ queryKey: ['chat-conversations', company], queryFn: () => chatService.listConversations(company) })
  const { data: dirData } = useQuery({ queryKey: ['chat-directory', company], queryFn: () => chatService.directory(company), staleTime: 60000 })
  const channels = convData?.data?.channels || []
  const docs = convData?.data?.docs || []
  const users = dirData?.data?.users || []

  const t = q.trim().toLocaleLowerCase('tr')
  const match = (v) => !t || (v || '').toLocaleLowerCase('tr').includes(t)
  const fCh = useMemo(() => channels.filter((c) => match(c.title)), [channels, q])
  const fDoc = useMemo(() => docs.filter((d) => match(d.title)), [docs, q])
  const fU = useMemo(() => users.filter((u) => !u.is_self && match(u.display_name)).slice(0, 12), [users, q])

  const toggle = (arr, set, id) => set(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id])
  const total = pickedCh.length + pickedU.length

  const send = async () => {
    if (!total || busy) return
    setBusy(true)
    try {
      const r = await chatService.forwardMessage(company, message.id, { channel_ids: pickedCh, user_ids: pickedU, note })
      toast.success('İletildi', `${r?.data?.count || 0} sohbete gönderildi`)
      onSent?.()
      onClose?.()
    } catch (e) {
      toast.error('Hata', e.response?.data?.detail || e.message)
    } finally { setBusy(false) }
  }

  const Row = ({ on, onClick, icon, label }) => (
    <button onClick={onClick} className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left hover:bg-gray-50 ${on ? 'bg-violet-50' : ''}`}>
      {icon}
      <span className="text-[13px] text-gray-800 truncate flex-1">{label}</span>
      {on && <Check size={14} className="text-violet-600 shrink-0" />}
    </button>
  )

  return createPortal(
    <div className="fixed inset-0 z-[85] flex items-center justify-center p-4" onMouseDown={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Mesajı ilet</h3>
          <button onClick={onClose} className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500"><X size={15} /></button>
        </div>

        <div className="px-4 py-2 border-b border-gray-50">
          <div className="px-3 py-2 rounded-lg bg-gray-50 text-[12px] text-gray-600 line-clamp-3">
            <span className="font-medium text-gray-700">{message.author_name}: </span>{message.body}
          </div>
        </div>

        <div className="p-3 overflow-y-auto flex-1">
          <div className="relative mb-2">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kanal veya kişi ara…"
              className="w-full pl-7 pr-2 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-violet-200" />
          </div>

          {fCh.length > 0 && <div className="px-1 py-1 text-[10px] font-semibold text-gray-400 uppercase">Kanallar</div>}
          {fCh.map((c) => (
            <Row key={`c${c.id}`} on={pickedCh.includes(c.id)} onClick={() => toggle(pickedCh, setPickedCh, c.id)}
              icon={<Hash size={13} className="text-gray-400 shrink-0" />} label={c.title} />
          ))}

          {fDoc.length > 0 && <div className="px-1 py-1 mt-1 text-[10px] font-semibold text-gray-400 uppercase">Belgeler</div>}
          {fDoc.map((d) => (
            <Row key={`d${d.id}`} on={pickedCh.includes(d.id)} onClick={() => toggle(pickedCh, setPickedCh, d.id)}
              icon={<FileText size={13} className="text-amber-500 shrink-0" />} label={d.title} />
          ))}

          {fU.length > 0 && <div className="px-1 py-1 mt-1 text-[10px] font-semibold text-gray-400 uppercase">Kişiler</div>}
          {fU.map((u) => (
            <Row key={`u${u.user_id}`} on={pickedU.includes(u.user_id)} onClick={() => toggle(pickedU, setPickedU, u.user_id)}
              icon={<span className={`w-5 h-5 rounded-full bg-gradient-to-br ${avatarGradient(u.display_name)} flex items-center justify-center text-[8px] font-semibold text-white shrink-0`}>{initials(u.display_name)}</span>}
              label={u.display_name} />
          ))}
        </div>

        <div className="px-4 py-3 border-t border-gray-100">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Not ekle (opsiyonel)…"
            className="w-full px-3 py-2 mb-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-violet-200" />
          <button onClick={send} disabled={!total || busy}
            className="w-full py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg disabled:opacity-40 flex items-center justify-center gap-1.5">
            <Send size={14} /> {busy ? 'Gönderiliyor…' : `İlet${total ? ` (${total})` : ''}`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
